import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useSponsors } from '../hooks/useSupabase';
import supabase from '../lib/supabase';
import SafeIcon from '../common/SafeIcon';
import ImageWithFallback from './ImageWithFallback';
import SponsorshipCTA from './SponsorshipCTA';
import * as FiIcons from 'react-icons/fi';

const {
  FiChevronLeft,
  FiChevronRight,
  FiExternalLink,
  FiHeart,
  FiShield,
  FiTrendingUp,
} = FiIcons;

const SponsorsCarousel = () => {
  const { data: sponsors, loading } = useSponsors();
  const [categories, setCategories] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [visibleCards, setVisibleCards] = useState(5);

  useEffect(() => {
    let cancelled = false;

    const loadCategories = async () => {
      const { data, error } = await supabase
        .from('sponsor_categories_aox1234')
        .select('slug,name,is_active,sort_order')
        .order('sort_order', { ascending: true });

      if (!cancelled && !error) setCategories(data || []);
    };

    loadCategories();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) setVisibleCards(1);
      else if (window.innerWidth < 768) setVisibleCards(2);
      else if (window.innerWidth < 1024) setVisibleCards(3);
      else setVisibleCards(5);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const displaySponsors = sponsors || [];
  const totalSlides = Math.max(1, displaySponsors.length - visibleCards + 1);
  const categoryLabel = (type) => categories.find((category) => category.slug === type)?.name || type || '';

  useEffect(() => {
    if (!isAutoPlaying || displaySponsors.length <= visibleCards) return undefined;

    const interval = window.setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalSlides);
    }, 4200);

    return () => window.clearInterval(interval);
  }, [isAutoPlaying, displaySponsors.length, visibleCards, totalSlides]);

  useEffect(() => {
    if (currentIndex >= totalSlides) setCurrentIndex(Math.max(0, totalSlides - 1));
  }, [currentIndex, totalSlides]);

  const nextSlide = () => setCurrentIndex((prev) => (prev + 1) % totalSlides);
  const prevSlide = () => setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);

  const openSponsor = (sponsor) => {
    if (!sponsor.url?.trim()) return;
    window.open(sponsor.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-secondary-900 via-primary-900 to-primary-700 py-20 text-white">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-20 -top-24 h-80 w-80 rounded-full border border-white/10" />
        <div className="absolute -left-3 -top-5 h-48 w-48 rounded-full border border-white/10" />
        <div className="absolute -bottom-32 right-0 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Sponsorship invitation - homepage placement #1 */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-14 grid items-center gap-8 rounded-[2rem] border border-white/15 bg-white/10 p-7 shadow-2xl backdrop-blur-xl md:grid-cols-[1fr_auto] md:p-10"
        >
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-black uppercase tracking-[0.18em] text-blue-100">
              <SafeIcon icon={FiHeart} className="h-4 w-4" />
              Η στήριξη γίνεται δύναμη
            </div>
            <h2 className="max-w-3xl text-3xl font-black leading-tight md:text-5xl">
              Στήριξε το σήμερα. Άλλαξε το αύριο του γυναικείου ποδοσφαίρου.
            </h2>
            <p className="mt-5 max-w-3xl text-base leading-relaxed text-blue-100 md:text-lg">
              Για εμάς ένας χορηγός δεν είναι ένα λογότυπο σε μια σελίδα. Είναι προπονήσεις, εξοπλισμός, μετακινήσεις, καλύτερες συνθήκες και περισσότερες ευκαιρίες για κάθε κορίτσι που επιλέγει να συνεχίσει να παίζει ποδόσφαιρο.
            </p>
          </div>

          <div className="md:min-w-[220px]">
            <SponsorshipCTA
              sourceLocation="home_sponsors_hero"
              variant="hero"
              label="Γίνε Χορηγός"
              className="w-full px-7 py-4 text-base"
            />
            <p className="mt-3 text-center text-xs text-white/60">
              3 στοιχεία · χωρίς τιμές · χωρίς δέσμευση
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-9 text-center"
        >
          <div className="mb-3 inline-flex items-center gap-2 text-sm font-black uppercase tracking-[0.2em] text-yellow-300">
            <SafeIcon icon={FiShield} className="h-4 w-4" />
            Οι άνθρωποι & οι επιχειρήσεις δίπλα μας
          </div>
          <h3 className="text-3xl font-black md:text-4xl">Οι Χορηγοί μας</h3>
          <p className="mx-auto mt-3 max-w-2xl text-blue-100">
            Ευχαριστούμε όσους επιλέγουν να επενδύσουν σε μια ομάδα, σε αθλήτριες και σε ένα άθλημα που αξίζει περισσότερο χώρο.
          </p>
        </motion.div>

        {loading ? (
          <div className="py-12 text-center">
            <div className="mx-auto h-9 w-9 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          </div>
        ) : displaySponsors.length > 0 ? (
          <div
            className="relative"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            {totalSlides > 1 && (
              <>
                <button
                  type="button"
                  onClick={prevSlide}
                  className="absolute -left-1 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white shadow-xl backdrop-blur-md transition hover:bg-white hover:text-primary-800 sm:-left-3"
                  aria-label="Προηγούμενοι χορηγοί"
                >
                  <SafeIcon icon={FiChevronLeft} className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={nextSlide}
                  className="absolute -right-1 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white shadow-xl backdrop-blur-md transition hover:bg-white hover:text-primary-800 sm:-right-3"
                  aria-label="Επόμενοι χορηγοί"
                >
                  <SafeIcon icon={FiChevronRight} className="h-5 w-5" />
                </button>
              </>
            )}

            <div className="mx-7 overflow-hidden sm:mx-8">
              <motion.div
                className="flex"
                animate={{
                  x: `-${currentIndex * (100 / Math.max(1, displaySponsors.length))}%`,
                }}
                transition={{ type: 'spring', stiffness: 180, damping: 24 }}
                style={{ width: `${Math.max(100, (displaySponsors.length / visibleCards) * 100)}%` }}
              >
                {displaySponsors.map((sponsor, index) => (
                  <motion.div
                    key={sponsor.id}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.04 }}
                    className="flex-shrink-0 px-2"
                    style={{ width: `${100 / Math.max(1, displaySponsors.length)}%` }}
                  >
                    <button
                      type="button"
                      onClick={() => openSponsor(sponsor)}
                      className={`group block w-full overflow-hidden rounded-2xl border border-white/15 bg-white text-left shadow-xl transition duration-300 hover:-translate-y-1 hover:shadow-2xl ${
                        sponsor.url ? 'cursor-pointer' : 'cursor-default'
                      }`}
                    >
                      <div className="relative flex aspect-[3/2] items-center justify-center bg-white p-5">
                        <ImageWithFallback
                          src={index >= Math.max(0, currentIndex - 1) && index < currentIndex + visibleCards + 1 ? sponsor.image_url : null}
                          imageWidth={480}
                          loading="lazy"
                          decoding="async"
                          alt={sponsor.name}
                          className="max-h-full max-w-full object-contain transition duration-300 group-hover:scale-105"
                          fallback={
                            <div className="text-center text-sm font-black text-gray-400">{sponsor.name}</div>
                          }
                        />
                        {sponsor.url && (
                          <div className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-primary-50 text-primary-600 opacity-0 transition group-hover:opacity-100">
                            <SafeIcon icon={FiExternalLink} className="h-4 w-4" />
                          </div>
                        )}
                      </div>
                      <div className="border-t border-gray-100 px-4 py-3 text-center">
                        <p className="truncate text-sm font-black text-gray-900">{sponsor.name}</p>
                        <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-500">
                          {categoryLabel(sponsor.type)}
                        </p>
                      </div>
                    </button>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {totalSlides > 1 && (
              <div className="mt-7 flex justify-center gap-2">
                {Array.from({ length: totalSlides }).map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setCurrentIndex(index)}
                    className={`h-2 rounded-full transition-all ${
                      index === currentIndex ? 'w-7 bg-yellow-300' : 'w-2 bg-white/30 hover:bg-white/50'
                    }`}
                    aria-label={`Σελίδα χορηγών ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="mx-auto max-w-2xl rounded-2xl border border-dashed border-white/20 bg-white/5 px-6 py-10 text-center text-blue-100">
            Η θέση των ανθρώπων και επιχειρήσεων που θα γράψουν μαζί μας το επόμενο κεφάλαιο είναι ανοιχτή.
          </div>
        )}

        {/* Homepage placement #2 inside the sponsor section; the sitewide floating CTA is placement #3 */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 flex flex-col items-center justify-between gap-6 rounded-2xl border border-white/10 bg-black/10 px-6 py-6 text-center md:flex-row md:text-left"
        >
          <div className="flex max-w-3xl items-start gap-4">
            <div className="hidden h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-300 text-yellow-950 sm:flex">
              <SafeIcon icon={FiTrendingUp} className="h-6 w-6" />
            </div>
            <div>
              <p className="text-lg font-black">Μια μικρή κίνηση για μια επιχείρηση μπορεί να είναι τεράστια για μια ομάδα.</p>
              <p className="mt-1 text-sm leading-relaxed text-blue-100">
                Μίλησέ μας. Χωρίς πακέτα και τιμές στο πρώτο βήμα — μόνο μια ανθρώπινη συζήτηση για το πώς μπορείς να σταθείς δίπλα στον ΑΟ Χανιά WFC.
              </p>
            </div>
          </div>
          <SponsorshipCTA
            sourceLocation="home_sponsors_bottom"
            variant="dark"
            label="Θέλω να στηρίξω"
            className="w-full flex-shrink-0 md:w-auto"
          />
        </motion.div>
      </div>
    </section>
  );
};

export default SponsorsCarousel;